/**
 * /sms-terms — the terms for Fugue Lab's text-message sign-in codes.
 *
 * US carriers verify a toll-free sending number against what the sender
 * publishes, and the registration (AWS End User Messaging,
 * US_TOLL_FREE_REGISTRATION) links here. So this page carries the mandatory
 * disclosures — program, opt-in, frequency, rates, STOP, HELP, carriers — and
 * must stay in step with what the product actually sends.
 * Spec: docs/pm/active/fugue-lab-sms-registration.md.
 */
import { Link } from 'react-router-dom'
import { useSEO } from '@/hooks/useSEO'

const SUPPORT_EMAIL = 'hello@fuguelab.com'
const LAST_UPDATED = 'September 19, 2026'

const h1 = 'text-3xl font-bold mb-4'
const h2 = 'text-xl font-semibold mt-10 mb-3'
const p = 'text-muted-foreground leading-relaxed mb-4'
const a = 'text-primary hover:underline'

export default function SmsTermsPage() {
  useSEO({
    title: 'SMS Terms',
    description: 'Terms for Fugue Lab sign-in codes sent by text message: what we send, how often, and how to stop.',
    url: '/sms-terms',
    canonical: '/sms-terms',
  })

  return (
    <article className="mx-auto max-w-3xl px-4 py-12">
      <h1 className={h1}>SMS Terms</h1>
      <p className="text-muted-foreground mb-8 text-sm">Last updated: {LAST_UPDATED}</p>

      <h2 className={h2}>The program</h2>
      <p className={p}>
        Fugue Lab is operated by Garrett John Law, a sole proprietor in Sherman Oaks, California. Fugue Lab sends
        one-time sign-in codes by text message to Fugue Lab account holders who choose to receive them. A code arrives
        when you sign in, or when you confirm your number. We never send marketing or promotional messages.
      </p>

      <h2 className={h2}>How you opt in</h2>
      <p className={p}>
        Only you can turn text codes on, from inside your account: Settings, then Security, then Text message codes. You
        enter your own mobile number and tick the consent box, and we text a code to that number. Your number is only
        used once you enter that code, which shows the phone is yours. We never add numbers any other way.
      </p>

      <h2 className={h2}>How often</h2>
      <p className={p}>
        One message each time you sign in or confirm a number, typically a few a month. Message and data rates may apply
        according to your mobile plan.
      </p>

      <h2 className={h2}>Stopping and help</h2>
      <p className={p}>
        Reply STOP to stop all messages; you will get one confirmation and nothing further. You can also turn text codes
        off in the same Security settings. Reply HELP, or email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={a}>
          {SUPPORT_EMAIL}
        </a>
        , for help.
      </p>

      <h2 className={h2}>Carriers</h2>
      <p className={p}>Mobile carriers are not liable for delayed or undelivered messages.</p>

      <h2 className={h2}>Privacy</h2>
      <p className={p}>
        Mobile numbers and SMS opt-in consent are never shared with or sold to third parties or affiliates for marketing
        or promotional purposes. See our{' '}
        <Link to="/privacy" className={a}>
          Privacy Policy
        </Link>
        .
      </p>
    </article>
  )
}
